/**
 * Supplement Facts Component
 * Handles scrolling to supplement facts section, expanding accordion, and dynamic font sizing
 */

class SupplementFacts {
  constructor() {
    this.isProcessing = false; // Flag to prevent concurrent operations
    this.fontSizingTimeout = null; // For debouncing font sizing
    this.scrollTimeout = null; // For debouncing scroll operations
    this.lastScrollCall = 0; // Track last scroll call time for proper debouncing
    this.init();
  }

  init() {
    // Make the scrollToSupplementFacts function globally available
    window.scrollToSupplementFacts = this.scrollToSupplementFacts.bind(this);
    
    // Apply dynamic font sizing on load and resize
    this.applyDynamicFontSizing();
    window.addEventListener('resize', () => {
      this.debouncedFontSizing();
    });
    
    // Listen for accordion clicks to trigger font sizing
    document.addEventListener('click', (e) => {
      if (e.target.closest('.cf-trigger')) {
        setTimeout(() => {
          this.debouncedFontSizing();
        }, 600); // Wait for accordion animation
      }
    });
    
    // Set up observer to watch for supplement facts visibility changes
    this.setupVisibilityObserver();
  }
  
  setupVisibilityObserver() {
    if (typeof IntersectionObserver !== 'undefined') {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && entry.target.closest('.cf-content.open')) {
            // Content is visible and accordion is open, apply font sizing
            setTimeout(() => {
              this.debouncedFontSizing();
            }, 100);
          }
        });
      });
      
      // Observe supplement facts elements when they appear
      const checkForElements = () => {
        const supplementElements = document.querySelectorAll('.supplement-facts-label');
        supplementElements.forEach(el => {
          if (!el.hasAttribute('data-observed')) {
            observer.observe(el);
            el.setAttribute('data-observed', 'true');
          }
        });
      };
      
      // Check now and on DOM changes
      checkForElements();
      if (typeof MutationObserver !== 'undefined') {
        const mutationObserver = new MutationObserver(checkForElements);
        mutationObserver.observe(document.body, { childList: true, subtree: true });
      }
    }
  }

  // Debounced font sizing to prevent multiple concurrent operations
  debouncedFontSizing() {
    if (this.fontSizingTimeout) {
      clearTimeout(this.fontSizingTimeout);
    }
    this.fontSizingTimeout = setTimeout(() => {
      this.applyDynamicFontSizing();
    }, 100);
  }

  scrollToSupplementFacts() {
    const currentTime = Date.now();
    
    // Implement proper debouncing - ignore calls within 300ms of each other
    if (currentTime - this.lastScrollCall < 300) {
      return;
    }
    
    this.lastScrollCall = currentTime;
    
    // Clear any existing scroll timeout
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }
    
    // Set a timeout to ensure this is the final call
    this.scrollTimeout = setTimeout(() => {
      this.executeScroll();
    }, 50); // Small delay to catch rapid clicks
  }

  executeScroll() {
    // Prevent multiple concurrent operations
    if (this.isProcessing) {
      return;
    }
    
    this.isProcessing = true;
    
    // Safety mechanism: reset processing flag after 3 seconds no matter what
    const safetyTimeout = setTimeout(() => {
      this.isProcessing = false;
    }, 3000);
    
    const resetProcessing = () => {
      clearTimeout(safetyTimeout);
      this.isProcessing = false;
    };
    
    // Find the supplement facts container - might not exist yet during page load
    const supplementFactsContent = document.querySelector('.supplement_fact_html');
    
    if (!supplementFactsContent) {
      // Element doesn't exist yet, wait for it to be rendered
      const checkInterval = setInterval(() => {
        const element = document.querySelector('.supplement_fact_html');
        if (element) {
          clearInterval(checkInterval);
          resetProcessing();
          // Retry the scroll after elements are found
          this.scrollToSupplementFacts();
        }
      }, 100);
      
      // Give up after 5 seconds
      setTimeout(() => {
        clearInterval(checkInterval);
        resetProcessing();
      }, 5000);
      return;
    }

    // Find the parent collapse item
    const cfItem = supplementFactsContent.closest('.cf-item');
    const cfContent = cfItem?.querySelector('.cf-content');
    const cfTrigger = cfItem?.querySelector('.cf-trigger');
    
    if (!cfItem || !cfContent || !cfTrigger) {
      // If not in a collapse structure, just scroll directly
      this.scrollToElement(supplementFactsContent);
      // Apply font sizing for direct scroll
      setTimeout(() => {
        this.applyDynamicFontSizing();
        resetProcessing();
      }, 100);
      return;
    }

    // Check if the section is currently collapsed
    const isCollapsed = !cfContent.classList.contains('open') || cfContent.style.maxHeight === '0px';
    
    if (isCollapsed) {
      // Manually expand the accordion instead of triggering click
      this.expandAccordion(cfContent, cfTrigger, cfItem);
      
      // Wait for the animation to complete, then apply font sizing and scroll
      setTimeout(() => {
        // Apply font sizing after expansion with a small delay to ensure rendering
        setTimeout(() => {
          this.applyDynamicFontSizing();
        }, 100);
        this.scrollToElement(supplementFactsContent);
        resetProcessing();
      }, 600); // Wait for the 0.5s transition + buffer
    } else {
      // Section is already expanded, apply font sizing and scroll
      this.applyDynamicFontSizing();
      this.scrollToElement(supplementFactsContent);
      resetProcessing();
    }
  }

  expandAccordion(cfContent, cfTrigger, cfItem) {
    // Close all other accordions in the same grid
    const cfGrid = cfItem.closest('.cf-grid');
    if (cfGrid) {
      cfGrid.querySelectorAll('.cf-trigger').forEach(trigger => trigger.classList.remove('open'));
      cfGrid.querySelectorAll('.cf-content').forEach(content => {
        content.style.maxHeight = '0px';
        content.classList.remove('open');
      });
    }
    
    // Open the target accordion
    cfTrigger.classList.add('open');
    cfContent.classList.add('open');
    cfContent.style.maxHeight = cfContent.scrollHeight + 'px';
  }

  applyDynamicFontSizing() {
    const supplementLabel = document.querySelector('.supplement-facts-label');
    
    if (!supplementLabel) {
      return;
    }

    const container = supplementLabel.closest('.supplement_fact_html');
    
    if (!container) {
      return;
    }

    // Check if the element is visible
    const isVisible = supplementLabel.offsetWidth > 0 && supplementLabel.offsetHeight > 0;
    
    // Check if it's in an open accordion
    const cfContent = container.closest('.cf-content');
    const isAccordionOpen = cfContent ? cfContent.classList.contains('open') : true;

    if (!isVisible || !isAccordionOpen) {
      return;
    }

    // Use the original autoFitFontSize approach for the h2 heading
    const heading = supplementLabel.querySelector('.supplement-facts-header h2');
    if (!heading) {
      return;
    }

    // Reset any existing transforms
    supplementLabel.style.transform = '';
    heading.style.fontSize = '';
    heading.style.letterSpacing = '';
    
    // Give the browser a moment to reflow
    requestAnimationFrame(() => {
      const containerWidth = container.offsetWidth;
      
      // Get the h2's parent element to account for any padding/margins
      const headerContainer = heading.parentElement;
      const headerContainerStyle = getComputedStyle(headerContainer);
      
      // Calculate available width accounting for padding and margins
      const paddingLeft = parseFloat(headerContainerStyle.paddingLeft) || 0;
      const paddingRight = parseFloat(headerContainerStyle.paddingRight) || 0;
      const marginLeft = parseFloat(headerContainerStyle.marginLeft) || 0;
      const marginRight = parseFloat(headerContainerStyle.marginRight) || 0;
      
      // Also account for h2's own margins
      const h2Style = getComputedStyle(heading);
      const h2MarginLeft = parseFloat(h2Style.marginLeft) || 0;
      const h2MarginRight = parseFloat(h2Style.marginRight) || 0;
      
      // Check for additional CSS properties that might affect width
      const h2PaddingLeft = parseFloat(h2Style.paddingLeft) || 0;
      const h2PaddingRight = parseFloat(h2Style.paddingRight) || 0;
      const h2BorderLeft = parseFloat(h2Style.borderLeftWidth) || 0;
      const h2BorderRight = parseFloat(h2Style.borderRightWidth) || 0;
      
      // Check container styles too
      const containerStyle = getComputedStyle(container);
      const containerPaddingLeft = parseFloat(containerStyle.paddingLeft) || 0;
      const containerPaddingRight = parseFloat(containerStyle.paddingRight) || 0;
      
      // Check supplement-facts-label styles
      const labelStyle = getComputedStyle(supplementLabel);
      const labelPaddingLeft = parseFloat(labelStyle.paddingLeft) || 0;
      const labelPaddingRight = parseFloat(labelStyle.paddingRight) || 0;
      const labelBorderLeft = parseFloat(labelStyle.borderLeftWidth) || 0;
      const labelBorderRight = parseFloat(labelStyle.borderRightWidth) || 0;
      
      const totalHorizontalSpacing = paddingLeft + paddingRight + marginLeft + marginRight + 
                                   h2MarginLeft + h2MarginRight + h2PaddingLeft + h2PaddingRight +
                                   h2BorderLeft + h2BorderRight + containerPaddingLeft + containerPaddingRight +
                                   labelPaddingLeft + labelPaddingRight + labelBorderLeft + labelBorderRight;
      
      // Use responsive buffer based on screen size (reduced for better fit)
      const isMobile = window.innerWidth <= 749;
      const buffer = isMobile ? 15 : 10; // Much more generous - was 30/35
      
      let availableWidth = containerWidth - totalHorizontalSpacing - buffer;
      
      // Safety check: if available width is too small, use a percentage of container width
      if (availableWidth < containerWidth * 0.5) {
        availableWidth = containerWidth * 0.85; // Use 85% of container width as fallback (more conservative)
      }
      
      // Reset to default and force proper sizing behavior
      heading.style.fontSize = '10px';
      heading.style.letterSpacing = '1px';
      heading.style.whiteSpace = 'nowrap';
      heading.style.overflow = 'visible';
      heading.style.width = 'auto';
      heading.style.maxWidth = 'none';
      heading.style.minWidth = '0';
      
      // Force the element to recalculate by temporarily changing display
      const originalDisplay = heading.style.display;
      heading.style.display = 'inline-block';
      
      // Force reflow to clear any cached measurements
      heading.offsetWidth;
      
      // Binary search for the perfect font size
      let minSize = 10;
      let maxSize = 100;
      let bestSize = minSize;
      
      while (minSize <= maxSize) {
        const midSize = Math.floor((minSize + maxSize) / 2);
        heading.style.fontSize = midSize + 'px';
        
        // Force reflow after each font size change
        heading.offsetWidth;
        
        const currentWidth = heading.scrollWidth;
        
        // Use full available width for maximum font size
        if (currentWidth <= availableWidth) {
          bestSize = midSize;
          minSize = midSize + 1;
        } else {
          maxSize = midSize - 1;
        }
      }
      
      // Set the final font size and restore display if changed
      heading.style.fontSize = bestSize + 'px';
      if (originalDisplay !== heading.style.display) {
        heading.style.display = originalDisplay || '';
      }
    });
  }

  scrollToElement(element) {
    // Ensure element is visible and accessible
    if (!element || !element.offsetParent) {
      // Element might not be visible yet, try again after a short delay
      setTimeout(() => {
        if (element && element.offsetParent) {
          this.scrollToElement(element);
        }
      }, 100);
      return;
    }

    // Check if page is still loading (images, fonts, etc.)
    const isPageLoading = document.readyState !== 'complete';
    
    // If page is loading, wait for it to complete before scrolling
    if (isPageLoading) {
      window.addEventListener('load', () => {
        // Extra delay to ensure layout is stable after load
        setTimeout(() => {
          this.performScroll(element);
        }, 200);
      }, { once: true });
      return;
    }
    
    // Page is loaded, perform scroll
    this.performScroll(element);
  }

  performScroll(element) {
    // Double-check element is still accessible
    if (!element || !element.offsetParent) {
      return;
    }

    const elementRect = element.getBoundingClientRect();
    const absoluteElementTop = elementRect.top + window.pageYOffset;
    const middle = absoluteElementTop - (window.innerHeight / 2);
    
    window.scrollTo({
      top: Math.max(0, middle), // Ensure we don't scroll to negative values
      behavior: 'smooth'
    });
  }
}

// Initialize when DOM is loaded or immediately if already loaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new SupplementFacts();
  });
} else {
  // DOM is already loaded
  new SupplementFacts();
}
